import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FadeUp } from "@/components/Reveal";

const FEATURED = [
  {
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_134b3b07-bdc2-4f4a-bd99-c12f6d023f68/artifacts/tqwrl555_Egle%201..webp",
    alt: "Egle T. holding her baby close on the lava rocks beside the ocean",
    quote:
      "Athena brought so much warmth, intuition, and gentle care into our home after the birth of my daughter. She seemed to naturally know when to help, when to step in, and when to simply give me space. She cared for both me and my baby — preparing nourishing meals, encouraging me to rest, and reminding me that I was enough. Her calm nurturing presence helped me to trust my own instincts as a new mother, and I always felt deeply respected, understood and cared for.",
    name: "Egle T.",
    detail: "Postpartum care in the days after birth",
  },
  {
    image:
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_1422a6f1-39bc-4477-9c8d-19873b26f4d8/artifacts/vztk0u7v_Stacey%20pic%20for%20website.webp",
    alt: "A mother wrapped in a mustard rebozo shawl in an old-town plaza",
    quote:
      "Testimony to come — a mother's words are being gathered and will rest here soon.",
    name: "Stacey",
    detail: "Testimony to come",
    placeholder: true,
  },
];

const QUOTES = [
  {
    quote:
      "I didn't know how empty I was running until someone finally filled my cup. Those six weeks changed how I mother.",
    name: "Priya S.",
    detail: "First Forty Days package",
  },
  {
    quote:
      "The warm meals gave me back myself — broths and stews appearing like magic while I rested with my baby. I cried at the closing ceremony.",
    name: "Danielle M.",
    detail: "Nourishing meal care",
  },
  {
    quote:
      "After a hard first birth experience, this time I was held. That made all the difference in the world.",
    name: "Ruth A.",
    detail: "Sacred companion care & belly binding",
  },
];

const AUTO_ADVANCE_MS = 8000;

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setIdx((v) => (v + 1) % FEATURED.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, [paused]);

  const current = FEATURED[idx];

  return (
    <section
      className="bg-ivory py-24 lg:py-32"
      data-testid="testimonials-section"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sienna">
            From the mothers
          </p>
          <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-espresso sm:text-5xl lg:text-6xl">
            Held, in their <span className="italic text-sienna">own words</span>
          </h2>
        </FadeUp>

        {/* Featured mothers — fading slideshow */}
        <div
          className="mt-20 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14"
          data-testid="featured-testimonial"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <FadeUp className="lg:col-span-5">
            <figure className="relative">
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-xl border border-espresso/15">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={current.image}
                    src={current.image}
                    alt={current.alt}
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.02 }}
                    transition={{ duration: 1.1, ease: "easeInOut" }}
                    className="absolute inset-0 h-full w-full object-cover"
                    data-testid="featured-testimonial-image"
                  />
                </AnimatePresence>
              </div>
              <span className="absolute -bottom-4 left-6 rounded-full bg-sienna px-4 py-1.5 text-[10px] font-medium uppercase tracking-[0.25em] text-ivory shadow-sm">
                A mother's story
              </span>
            </figure>
          </FadeUp>

          <FadeUp delay={0.15} className="flex flex-col justify-center lg:col-span-7">
            <span
              aria-hidden
              className="font-serif text-6xl leading-none text-sienna/60 lg:text-8xl"
            >
              &ldquo;
            </span>
            <div className="relative min-h-[260px] sm:min-h-[220px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.name}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.9, ease: "easeInOut" }}
                >
                  <blockquote
                    className={`mt-2 font-serif text-2xl font-light italic leading-snug text-espresso sm:text-3xl lg:text-4xl ${
                      current.placeholder ? "text-espresso/60" : ""
                    }`}
                    data-testid="featured-testimonial-quote"
                  >
                    {current.quote}
                  </blockquote>
                  <figcaption className="mt-8 flex items-center gap-4 text-xs font-medium uppercase tracking-[0.22em] text-cocoa">
                    <span className="h-px w-10 bg-sienna" aria-hidden />
                    <span>
                      {current.name}
                      {current.detail && (
                        <span className="text-sienna"> · {current.detail}</span>
                      )}
                    </span>
                  </figcaption>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Dot indicators */}
            <div
              className="mt-8 flex items-center gap-3"
              role="tablist"
              aria-label="Featured mothers"
              data-testid="featured-testimonial-dots"
            >
              {FEATURED.map((m, i) => (
                <button
                  key={m.name}
                  type="button"
                  role="tab"
                  aria-selected={i === idx}
                  aria-label={`Show ${m.name}'s story`}
                  onClick={() => setIdx(i)}
                  data-testid={`featured-dot-${i}`}
                  className={`h-2 rounded-full transition-all duration-500 ${
                    i === idx ? "w-10 bg-sienna" : "w-2 bg-sienna/30 hover:bg-sienna/60"
                  }`}
                />
              ))}
            </div>
          </FadeUp>
        </div>

        {/* Secondary quotes grid */}
        <div className="mt-24 grid grid-cols-1 gap-12 border-t border-espresso/15 pt-16 lg:grid-cols-3 lg:gap-8">
          {QUOTES.map((q, i) => (
            <FadeUp key={q.name} delay={0.08 * i}>
              <figure
                className={i === 1 ? "lg:mt-12" : ""}
                data-testid={`testimonial-${i}`}
              >
                <blockquote className="font-serif text-2xl font-light italic leading-snug text-espresso">
                  &ldquo;{q.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-6 text-xs font-medium uppercase tracking-[0.2em] text-cocoa">
                  {q.name} · <span className="text-sienna">{q.detail}</span>
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
